const formTab = [
  {
    id: 1,
    label: 'Information',
  },
  {
    id: 2,
    label: 'Pricing & Rates',
  },
  {
    id: 3,
    label: 'Availability Settings',
  },
];

/**
 * The three states a record carries. `suspended` is what Delete writes — the
 * record is not gone, so it needs a name and a place in the filters rather
 * than silently dropping out of both the active and inactive lists.
 */
const inventoryStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const inventoryStatusValues = inventoryStatusOptions.map(
  option => option.value
);

const resourceFormTab = [
  {
    id: 1,
    label: 'General',
  },
  {
    id: 2,
    label: 'Schedule',
  },
  {
    id: 3,
    label: 'Smart Pricing',
  },
];

const ACTIVITY_GENERAL_TAB = 1;
const ACTIVITY_WIDGET_TAB = 4;
const ACTIVITY_PRICING_TAB = 5;

const activityFormTab = [
  { id: ACTIVITY_GENERAL_TAB, label: 'General' },
  { id: ACTIVITY_PRICING_TAB, label: 'Pricing' },
  { id: ACTIVITY_WIDGET_TAB, label: 'Widget' },
];

// Fields rendered on the activity Widget tab: a save that fails only on
// these switches to that tab so the error is visible.
const activityWidgetFields = [
  'promoLabel',
  'color',
  'description',
  'usp',
  'images',
  'categories',
  'isPopular',
];

// Fields rendered on the activity Pricing tab, as error paths.
const activityPricingFields = [
  'taxPercentage',
  'priceVariant',
  'resource.option',
  'resource.ageGroups',
  'resource.groupSizes',
];

// Duration quick picks on the Pricing tab. Unlike age groups and group sizes
// (Settings -> Company), there is no company setting for these yet, so they are
// fixed here; "Custom" adds a blank row.
const durationPresetMinutes = [30, 60, 90, 120, 180, 240];

const foodAndBeverageTab = [
  {
    id: 1,
    label: 'General',
  },
  {
    id: 2,
    label: 'Schedule',
  },
  {
    id: 3,
    label: 'Smart Pricing',
  },
];

const visibilityOptions = [
  {
    value: 'CMS',
    label: 'CMS',
  },
  {
    value: 'CMS & Direct Link',
    label: 'CMS & Direct Link',
  },
  {
    value: 'CMS, Direct Link & Widget',
    label: 'CMS, Direct Link & Widget',
  },
];

const productSettingOptions = [
  {
    value: 'primary activity',
    label: 'Primary',
  },
  {
    value: 'secondary activity',
    label: 'Secondary',
  },
  {
    value: 'both',
    label: 'Both',
  },
];

const productSettingLabel = (value?: string | null) =>
  productSettingOptions.find(option => option.value === value)?.label ?? '-';

/**
 * Whether a product may occupy a booking's main-activity slot, per its
 * "Selectable As" setting.
 *
 * Deliberately a not-equals rather than an allow-list: `productSetting` is only
 * guaranteed on the activity *detail* response, so a list payload that does not
 * carry it yet must leave every activity selectable rather than emptying the
 * picker. Fails open on undefined for the same reason older records do.
 */
const isSelectableAsPrimary = (productSetting?: string | null) =>
  productSetting !== 'secondary activity';

/**
 * Which booking flow a product may be sold through: the parallel day builder,
 * the standalone step-by-step booking, or either. Sibling of "Selectable As",
 * which answers *where in one booking* the product may sit rather than *which
 * booking form* may offer it.
 *
 * The standalone option is stored as `Single` — the value the API already
 * speaks — and only relabelled for staff, so the gates below and every payload
 * keep matching on `single`.
 */
const bookingOptionOptions = [
  {
    value: 'Parallel',
    label: 'Parallel',
  },
  {
    value: 'Single',
    label: 'Standalone',
  },
  {
    value: 'Both',
    label: 'Both',
  },
];

const bookingOptionLabel = (value?: string | null) =>
  bookingOptionOptions.find(option => option.value === value)?.label ?? '-';

/**
 * Whether a product may be placed alongside another activity, i.e. as a
 * parallel lane in the day builder.
 *
 * Judged per *placement*, not per screen: the day builder is also the edit
 * page's schedule editor, so "which page am I on" says nothing about how the
 * item being drafted actually sits in the day. A day's opening activity is a
 * new time slot even on the parallel page, which is what makes a
 * Parallel-only product a companion product — it is offered only next to
 * something already on the timeline, never as the item that opens the day.
 *
 * Compared case-insensitively because these values are capitalised while
 * `productSetting`'s are not, and fails open on a missing value for the same
 * reason the Selectable As gates do: `bookingOption` is not guaranteed on a
 * list payload, and an absent field must leave the picker full rather than
 * empty it.
 */
const isBookableInParallel = (bookingOption?: string | null) =>
  bookingOption?.toLowerCase() !== 'single';

/**
 * The mirror of isBookableInParallel: whether a product may take a time slot
 * of its own: any day-builder item that is not running alongside another
 * activity.
 *
 * Fails open on a missing setting for the same reason the parallel gate does.
 */
const isBookableInSingle = (bookingOption?: string | null) =>
  bookingOption?.toLowerCase() !== 'parallel';

const exclusivityOptions = [
  {
    value: 'booking',
    label: 'Booking',
  },
  {
    value: 'activity',
    label: 'Activity',
  },
];

const resourceType = [
  {
    value: 'inventory base',
    label: 'Inventory Based',
  },
  {
    value: 'space base',
    label: 'Space Base',
  },
];

const calendarOptions = [
  {
    value: 'google calendar',
    label: 'Google Calendar',
  },
];

const priceVariantResourceOptions = [
  {
    label: 'Per Person',
    value: 'per person',
  },
  {
    label: 'Per Resource',
    value: 'per resource',
  },
];

const participantTypeOptions = [
  {
    label: 'Per Person',
    value: 'Per Person',
  },
  {
    label: 'Group',
    value: 'Group',
  },
];

const priceVariantActivityOptions = [
  {
    label: 'Per Person',
    value: 'Per Person',
  },
  {
    label: 'Per Unit',
    value: 'Per Unit',
  },
];

const addOnVisibilityOptions = [
  {
    label: 'Upsell only',
    value: 'upsell',
  },
  {
    label: 'Add-on only',
    value: 'addon',
  },
  {
    label: 'Both',
    value: 'both',
  },
];

const visibilityValues = ['upsell', 'addon', 'both'] as const;

type Visibility = (typeof visibilityValues)[number];

/** Maps the single `visibility` form field to the API's two booleans. */
const visibilityToFlags = (
  visibility?: string | null
): { upsell: boolean; addon: boolean } => {
  switch (visibility) {
    case 'upsell':
      return { upsell: true, addon: false };
    case 'both':
      return { upsell: true, addon: true };
    default:
      return { upsell: false, addon: true };
  }
};

/**
 * Maps the API's two booleans back to the `visibility` form field.
 * Legacy records (both false, or `addon` missing) fall back to 'addon'.
 */
const flagsToVisibility = (
  upsell?: boolean | string | null,
  addon?: boolean | string | null
): Visibility => {
  const isUpsell = upsell === true || upsell === 'true';
  const isAddon = addon === true || addon === 'true';

  if (isUpsell && isAddon) return 'both';
  if (isUpsell) return 'upsell';

  return 'addon';
};

const resetTimeOptions = [
  {
    label: 'Not Used',
    value: 'not used',
  },
  {
    label: 'Block All Capacity',
    value: 'block all capacity',
  },
  {
    label: 'Block Used Capacity',
    value: 'block used capacity',
  },
];

const addOnTypeOptions = [
  {
    value: 'Before Activity',
    label: 'Before Activity',
  },
  {
    value: 'After Activity',
    label: 'After Activity',
  },
];

const avatarColorOptions = [
  {
    value: '#E74C3C',
    label: 'Alizarin',
  },
  {
    value: '#C0392B',
    label: 'Pomegranate',
  },
  {
    value: '#D35400',
    label: 'Pumpkin',
  },
  {
    value: '#E67E22',
    label: 'Carrot',
  },
  {
    value: '#F39C12',
    label: 'Orange',
  },
  {
    value: '#F1C40F',
    label: 'Sun Flower',
  },
  {
    value: '#27AE60',
    label: 'Nephritis',
  },
  {
    value: '#2ECC71',
    label: 'Emerald',
  },
  {
    value: '#16A085',
    label: 'Green Sea',
  },
  {
    value: '#1ABC9C',
    label: 'Sky Blue',
  },

  {
    value: '#2980B9',
    label: 'Belize Hole',
  },
  {
    value: '#3498DB',
    label: 'Peter River',
  },
  {
    value: '#34495E',
    label: 'Wet Asphalt',
  },
  {
    value: '#2C3E50',
    label: 'Midnight Blue',
  },
  {
    value: '#8E44AD',
    label: 'Wisteria',
  },
  {
    value: '#9B59B6',
    label: 'Amethyst',
  },
  {
    value: '#ECF0F1',
    label: 'Clouds',
  },
  {
    value: '#BDC3C7',
    label: 'Silver',
  },
  {
    value: '#95A5A6',
    label: 'Concrete',
  },
  {
    value: '#7F8C8D',
    label: 'Asbestos',
  },
];

export {
  formTab,
  visibilityOptions,
  productSettingOptions,
  productSettingLabel,
  isSelectableAsPrimary,
  bookingOptionOptions,
  bookingOptionLabel,
  isBookableInParallel,
  isBookableInSingle,
  exclusivityOptions,
  resourceType,
  calendarOptions,
  participantTypeOptions,
  priceVariantResourceOptions,
  priceVariantActivityOptions,
  resetTimeOptions,
  addOnTypeOptions,
  avatarColorOptions,
  resourceFormTab,
  ACTIVITY_GENERAL_TAB,
  ACTIVITY_WIDGET_TAB,
  ACTIVITY_PRICING_TAB,
  activityFormTab,
  activityWidgetFields,
  activityPricingFields,
  durationPresetMinutes,
  inventoryStatusOptions,
  inventoryStatusValues,
  foodAndBeverageTab,
  addOnVisibilityOptions,
  visibilityValues,
  visibilityToFlags,
  flagsToVisibility,
};
export type { Visibility };
